/* PPA Warehouse Label (slim, event-driven, no polling/observers)
   - Reads #ppa-variant-warehouse-map JSON (usa/can counts per variant)
   - Recomputes ship_from when: variant changes OR you call setCountry('US'|'CA'|..)
   - Keeps hidden inputs in sync (outside form, via `form` attr)
   - Updates visible label nodes: #ppa-warehouse-label, [data-ppa-warehouse-label], .ppa-warehouse-label
   - Toggles `ppa-tariff-needed` when country === 'US' && ship_from === 'Canada'

   Public:
     PPA_WAREHOUSE_LABEL.update()                 // recompute from current variant & country
     PPA_WAREHOUSE_LABEL.setCountry('US'|'CA'|..) // override detected country + update
     PPA_WAREHOUSE_LABEL.set({ship_from, origin_country, usmca_qualifies})
     PPA_WAREHOUSE_LABEL.get()
*/
(function () {
  'use strict';
  if (window._ppa_warehouse_label_loaded) return;
  window._ppa_warehouse_label_loaded = true;

  // ------------ utils ------------
  const upper = s => String(s||'').toUpperCase();
  const lower = s => String(s||'').toLowerCase();
  const toInt = v => { const n = parseInt(String(v||'').replace(/[^0-9-]/g,''),10); return isNaN(n)?0:n; };

  const onReady = fn => (document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once:true })
    : fn());

  function toggleTariffClass(active){
    try {
      const root = document.documentElement || document.body;
      active ? root.classList.add('ppa-tariff-needed') : root.classList.remove('ppa-tariff-needed');
    } catch {}
  }

  // ------------ DOM helpers ------------
  function productForms(){
    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
    const seen = new Set();
    return forms.filter(f => { if(!f.id) return true; if(seen.has(f.id)) return false; seen.add(f.id); return true; });
  }
  function ensureHidden(formId,name){
    if(!formId) return null;
    const id = 'ppa-prop-' + name.replace(/[^a-z0-9_\-\[\]]/gi,'');
    let el = document.getElementById(id);
    if (el && el.getAttribute('form') === formId) return el;
    el = document.querySelector(`input[name="${name}"][form="${CSS.escape(formId)}"]`);
    if (el) return el;
    el = document.createElement('input');
    el.type = 'hidden'; el.name = name; el.id = id; el.setAttribute('form', formId);
    document.body.appendChild(el);
    return el;
  }
  function ensureAllHidden(){
    productForms().forEach(f=>{
      const id = f.id||'';
      ensureHidden(id,'properties[ship_from]');
      ensureHidden(id,'properties[origin_country]');
      ensureHidden(id,'properties[usmca_qualifies]');
    });
  }
  function paintVisibleLabel(shipFrom){
    const nodes = [
      document.getElementById('ppa-warehouse-label'),
      ...document.querySelectorAll('[data-ppa-warehouse-label], .ppa-warehouse-label')
    ].filter(Boolean);
    const txt = `Ships from ${shipFrom} warehouse`;
    nodes.forEach(n=>{ n.textContent = txt; n.setAttribute('data-ship-from', shipFrom); });
  }

  // ------------ data sources ------------
  function parseMap(){
    try {
      const node = document.getElementById('ppa-variant-warehouse-map');
      if (!node) return null;
      const raw = JSON.parse(node.textContent || node.innerText || '{}');
      const norm = {};
      Object.keys(raw).forEach(k=>{
        const v = raw[k]||{};
        norm[String(k)] = { usa: toInt(v.usa), can: toInt(v.can) };
      });
      return norm;
    } catch { return null; }
  }
  function currentVariantId(){
    const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (idInput?.value) return String(idInput.value);
    try { const sid = window.ShopifyAnalytics?.meta?.selectedVariantId; if (sid) return String(sid); } catch {}
    const m = location.search.match(/[?&]variant=([0-9]+)/);
    return m ? String(m[1]) : null;
  }

  // ------------ decision logic ------------
  function decideWarehouse(entry, destISO){
    const usa = toInt(entry?.usa), can = toInt(entry?.can), isUS = upper(destISO) === 'US';
    if (isUS)  return usa>0 ? 'USA'    : (can>0 ? 'Canada' : 'Canada');
    return       can>0 ? 'Canada' : (usa>0 ? 'USA'    : 'USA');
  }

  // ------------ state ------------
  let COUNTRY = upper(window.PPA_COUNTRY || document.querySelector('meta[name="shopify-country-code"]')?.content || '');
  let _state = { ship_from:'', origin_country:'', usmca_qualifies:'false' };

  function applyState(next){
    _state = {
      ship_from: next.ship_from ?? _state.ship_from,
      origin_country: next.origin_country ?? _state.origin_country,
      usmca_qualifies: String(next.usmca_qualifies ?? _state.usmca_qualifies) === 'true' ? 'true' : 'false'
    };
    // write hidden props
    productForms().forEach(f=>{
      const id = f.id||'';
      const s = ensureHidden(id,'properties[ship_from]');
      const o = ensureHidden(id,'properties[origin_country]');
      const u = ensureHidden(id,'properties[usmca_qualifies]');
      if (s && s.value !== _state.ship_from) s.value = _state.ship_from;
      if (o && o.value !== _state.origin_country) o.value = _state.origin_country;
      if (u && u.value !== _state.usmca_qualifies) u.value = _state.usmca_qualifies;
    });
    paintVisibleLabel(_state.ship_from);
    toggleTariffClass(upper(COUNTRY)==='US' && lower(_state.ship_from)==='canada');
  }

  function recompute(){
    const map = parseMap(); if (!map) return;
    const vid = currentVariantId(); if (!vid) return;
    const entry = map[String(vid)]; if (!entry) return;
    const shipFrom = decideWarehouse(entry, COUNTRY);
    const origin   = shipFrom === 'USA' ? 'US' : 'CA';
    if (shipFrom !== _state.ship_from || origin !== _state.origin_country){
      applyState({ ship_from: shipFrom, origin_country: origin, usmca_qualifies:'false' });
    }
  }

  // ------------ wire up minimal events ------------
  function attachVariantListeners(){
    // Dawn and many themes dispatch this when variant changes:
    document.addEventListener('variant:change', recompute, { passive:true });
    // Fallback: listen to id input/select changes
    const els = document.querySelectorAll('form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select, form[action*="/cart/add"] input[type="radio"]');
    els.forEach(el=>{
      el.addEventListener('change', recompute, { passive:true });
      el.addEventListener('input',  recompute, { passive:true });
    });
  }

  // ------------ public API ------------
  window.PPA_WAREHOUSE_LABEL = window.PPA_WAREHOUSE_LABEL || {};
  window.PPA_WAREHOUSE_LABEL.update = function(){ try{ recompute(); }catch{} };
  window.PPA_WAREHOUSE_LABEL.setCountry = function(iso){
    const next = upper(iso||'');
    if (next && next !== COUNTRY){ COUNTRY = next; recompute(); }
  };
  window.PPA_WAREHOUSE_LABEL.set = function (payload) {
    payload = payload || {};
    applyState({
      ship_from: payload.ship_from,
      origin_country: payload.origin_country,
      usmca_qualifies: payload.usmca_qualifies
    });
  };
  window.PPA_WAREHOUSE_LABEL.get = function(){ return { ..._state, country: COUNTRY }; };

  // ------------ init ------------
  onReady(function(){
    ensureAllHidden();
    attachVariantListeners();
    // bootstrap country (prefer window.PPA_COUNTRY if you already set it elsewhere)
    if (!COUNTRY) COUNTRY = 'US'; // safe default; override by calling setCountry(...)
    recompute(); // first paint from current variant + country
  });
})();
